import { all, dropTask, task, timeout } from 'ember-concurrency';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

const CONVERSATIONS_PER_PAGE = 30;
const TOKEN_REFRESH_INTERVAL_MINS = 4.5;

export function pageNumberInfinityOptions(perPage = CONVERSATIONS_PER_PAGE) {
  return {
    startingPage: 1,
    perPage,
    perPageParam: 'page[size]',
    pageParam: 'page[number]',
  };
}

export default class MessagingService extends Service {
  @service infinity;
  @service intl;
  @service messagingNavigation;
  @service store;

  @tracked conversations;
  @tracked currentUser;
  @tracked messagingApiURL = '';
  @tracked token;

  get tokenRefreshIntervalMins() {
    return isEmberTesting() ? 0 : TOKEN_REFRESH_INTERVAL_MINS;
  }

  async start(messagingApiURL = '') {
    this.messagingApiURL = messagingApiURL;
    await this.loadApp.perform();
  }

  loadApp = task(async () => {
    await this.getMessagingToken.perform();
    await all([this.getCurrentUser.perform(), this.getConversations.perform()]);
    this.pollMessagingToken.perform();
  });

  pollMessagingToken = task(async () => {
    do {
      await timeout(this.tokenRefreshIntervalMins * 60 * 1000);
      await this.getMessagingToken.perform();
    } while (!isEmberTesting());
  });

  getMessagingToken = task(async () => {
    const response = await fetch('/messaging/tokens', {
      method: 'POST',
    });
    const json = await response.json();
    this.token = json.token;
  });

  getCurrentUser = task(async () => {
    this.currentUser = await this.store.queryRecord('messaging-profile', {
      include: 'system_user',
    });
    this.messagingNavigation.text = this.intl.t('navigation.conversation_list', {
      amount: this.currentUser.unreadConversationIds.length,
    });
  });

  getConversations = task(async () => {
    const model = await this.infinity.model('messaging-conversation', {
      include: 'users,last_message',
      ...pageNumberInfinityOptions(),
    });
    this.updateConversations(model);
  });

  loadMoreConversations = dropTask(async model => {
    let data = await this.infinity.loadNextPage(model);

    this.updateConversations(data);
  });

  updateConversations(data) {
    this.conversations = this.infinity.replace(
      data,
      data.filter(conversation => !conversation.disabled)
    );
  }
}
